import {ObservationCard} from "./observation-card";
import {ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import {DateTime} from 'luxon';
import {AppContext} from "../App";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarDays, faChevronLeft, faChevronRight, faClock, faHashtag, faLocationDot} from "@fortawesome/free-solid-svg-icons";
import type {SpeciesFamilyLink} from "../App";
import {getMacaulayCatalogNumbers} from "../helpers/macaulayLibrary";
import {buildHotspotDetailPath} from "../views/hotspot-explorer/helpers";
import {Link} from "react-router-dom";
import type {SpeciesMetadataEntry} from "../helpers/speciesMetadataLookup";
import {formatDate} from "../helpers/helpers";
import {PRE_EBIRD_LIST_BUILDING_YEAR} from "../helpers/storedBirdData";
import {PRE_EBIRD_LIST_BUILDING_MAP_PERIOD_LABEL} from "../views/map/constants";
import './infobox-single-observation.scss';

export type InfoboxObservationCardProps = {
    currentMarker: any,
    currentIndex: number,
    total: number,
    year?: string|number,
    month?: string|number,
    familyLink?: SpeciesFamilyLink,
    familyByTaxonomicOrder?: Map<number, SpeciesFamilyLink>,
    hotspotMarkers?: any[],
    hotspotIndex?: number,
    hotspotTotal?: number,
    onPreviousHotspotMarker?: () => void,
    onNextHotspotMarker?: () => void,
    onClose?: () => void
}

export function InfoboxSingleObservation({
    currentMarker,
    currentIndex,
    total,
    year,
    month,
    familyLink,
    familyByTaxonomicOrder,
    hotspotMarkers = [],
    hotspotIndex = 0,
    hotspotTotal = 0,
    onPreviousHotspotMarker,
    onNextHotspotMarker,
    onClose
}: InfoboxObservationCardProps) {
    const [intro, setIntro] = useState<ReactNode>();
    const [title, setTitle] = useState<ReactNode>();
    const [image, setImage] = useState<string>();
    const [kicker, setKicker] = useState<string>();
    const [copy, setCopy] = useState<ReactNode>();
    const [sourceUrl, setSourceUrl] = useState<string>();
    const [badges, setBadges] = useState<string[]>([]);
    const [macaulayCatalogNumbers, setMacaulayCatalogNumbers] = useState<string[]>([]);
    const [measuredCardHeight, setMeasuredCardHeight] = useState<number>();
    const measureCardRefs = useRef<Array<HTMLDivElement | null>>([]);

    const appContext = useContext(AppContext);
    const showHotspotControls = Boolean(hotspotTotal > 1 && onPreviousHotspotMarker && onNextHotspotMarker);
    const periodLabel = Number(year) === PRE_EBIRD_LIST_BUILDING_YEAR
        ? PRE_EBIRD_LIST_BUILDING_MAP_PERIOD_LABEL
        : year === 'life'
        ? 'your life list'
        : year;

    const getMarkerCardData = (marker: any, markerIndex = currentIndex) => {
        if (!marker?.properties) return undefined;

        const props = marker.properties;
        const markerFamilyLink = familyByTaxonomicOrder?.get(props.taxonomicOrder) ?? familyLink;
        const taxonomyResult: SpeciesMetadataEntry|undefined = appContext.lookupByTaxonomicOrder(props.taxonomicOrder);
        const dateLong = formatDate(props.date);
        const parsedTime = props.time ? DateTime.fromFormat(props.time, 'HH:mm') : undefined;
        const time = parsedTime?.isValid ? parsedTime.toLocaleString(DateTime.TIME_SIMPLE) : props.time;
        const locationId = props.locationId;
        const locationLabel = [props.locationName, props.locationName2].filter(Boolean).join(' - ');
        const observationBadges = [];

        if (props.isLifer) observationBadges.push('Lifer');
        else if (props.isFirstOfYear) observationBadges.push('First of Year');

        return {
            image: props.image,
            kicker: `${markerIndex + 1}/${total} for ${periodLabel}`,
            title: props.species,
            copy: (
                <>
                    <span className="observation-card__meta-line">
                        <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                        <span>{dateLong}</span>
                    </span>
                    {time ?
                        <span className="observation-card__meta-line">
                            <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                            <span>{time}</span>
                        </span> :
                        null
                    }
                    <span className="observation-card__meta-line">
                        <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                        {locationId ? (
                            <Link className="observation-card__meta-link" to={buildHotspotDetailPath(locationId)}>
                                {locationLabel}
                            </Link>
                        ) : (
                            <span>{locationLabel}</span>
                        )}
                    </span>
                    <span className="observation-card__meta-line observation-card__meta-line--count">
                        <FontAwesomeIcon icon={faHashtag} aria-hidden="true" />
                        <span>Count: {props.count}</span>
                    </span>
                </>
            ),
            badges: observationBadges,
            familyLink: markerFamilyLink ? {
                label: markerFamilyLink.familyName,
                to: `/family-explorer/family/${markerFamilyLink.familySlug}`
            } : undefined,
            sourceUrl: taxonomyResult && markerFamilyLink
                ? `/family-explorer/family/${markerFamilyLink.familySlug}/species/${taxonomyResult.speciesCode}`
                : undefined,
            macaulayCatalogNumbers: getMacaulayCatalogNumbers(props.mlCatalogNumbers)
        };
    }

    useEffect(() => {
        if (!currentMarker?.properties) return;
        const props = currentMarker.properties;

        const taxonomyResult: SpeciesMetadataEntry|undefined = appContext.lookupByTaxonomicOrder(props.taxonomicOrder);

        setTitle(props.species);
        setSourceUrl(
            taxonomyResult && familyLink
                ? `/family-explorer/family/${familyLink.familySlug}/species/${taxonomyResult.speciesCode}`
                : undefined
        );

        setImage(props.image);
        setMacaulayCatalogNumbers(getMacaulayCatalogNumbers(props.mlCatalogNumbers));

        const dateLong = formatDate(props.date);
        const parsedTime = props.time ? DateTime.fromFormat(props.time, 'HH:mm') : undefined;
        const time = parsedTime?.isValid ? parsedTime.toLocaleString(DateTime.TIME_SIMPLE) : props.time;
        setCopy(
            <>
                <span className="observation-card__meta-line">
                    <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                    <span>{dateLong}</span>
                </span>
                {time ?
                    <span className="observation-card__meta-line">
                        <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                        <span>{time}</span>
                    </span> :
                    null
                }
                <span className="observation-card__meta-line">
                    <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                    {props.locationId ? (
                        <Link className="observation-card__meta-link" to={buildHotspotDetailPath(props.locationId)}>
                            {[props.locationName, props.locationName2].filter(Boolean).join(' - ')}
                        </Link>
                    ) : (
                        <span>{[props.locationName, props.locationName2].filter(Boolean).join(' - ')}</span>
                    )}
                </span>
                <span className="observation-card__meta-line observation-card__meta-line--count">
                    <FontAwesomeIcon icon={faHashtag} aria-hidden="true" />
                    <span>Count: {props.count}</span>
                </span>
            </>
        )

        if (currentIndex + 1 && total) {
            setKicker(`${currentIndex + 1}/${total} for ${periodLabel}`);
        }

        const observationBadges = [];
        if (props.isLifer) observationBadges.push('Lifer');
        else if (props.isFirstOfYear) observationBadges.push('First of Year');
        setIntro(undefined);
        setBadges(observationBadges);
    }, [currentMarker, total, currentIndex, appContext, familyLink, periodLabel]);

    useLayoutEffect(() => {
        if (!showHotspotControls) {
            setMeasuredCardHeight(undefined);
            return;
        }

        const currentMeasurementCards = measureCardRefs.current.slice(0, hotspotMarkers.length);
        const nextMeasuredCardHeight = Math.max(
            ...currentMeasurementCards
                .map(card => card?.offsetHeight ?? 0)
                .filter(Boolean),
            0
        );

        setMeasuredCardHeight(nextMeasuredCardHeight || undefined);
    }, [showHotspotControls, hotspotMarkers, currentMarker, total, periodLabel, familyByTaxonomicOrder, familyLink, appContext]);

    return (
        <>
            {intro ?
                <h2 className="infobox__title">{intro}</h2>
                : null
            }

            <div
                className="infobox-single-observation__card-wrap"
                style={measuredCardHeight ? {height: `${measuredCardHeight}px`} : undefined}
            >
                <ObservationCard
                    image={image}
                    kicker={kicker}
                    title={title}
                    copy={copy}
                    badges={badges}
                    familyLink={familyLink ? {
                        label: familyLink.familyName,
                        to: `/family-explorer/family/${familyLink.familySlug}`
                    } : undefined}
                    sourceUrl={sourceUrl}
                    sourceLabel="Species Detail"
                    macaulayCatalogNumbers={macaulayCatalogNumbers}
                    onClose={onClose}
                    closeLabel="Close species card"
                />
            </div>
            <div className={`infobox-single-observation__hotspot-controls-wrap ${showHotspotControls ? '' : 'is-hidden'}`}>
                {showHotspotControls ?
                    <div className="infobox-single-observation__hotspot-controls" aria-label="Species at this hotspot">
                        <button
                            type="button"
                            className="infobox-single-observation__hotspot-button"
                            onClick={onPreviousHotspotMarker}
                            aria-label="Previous species at this hotspot"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                        </button>
                        <span className="infobox-single-observation__hotspot-label">
                            {hotspotIndex + 1}/{hotspotTotal} at this hotspot
                        </span>
                        <button
                            type="button"
                            className="infobox-single-observation__hotspot-button"
                            onClick={onNextHotspotMarker}
                            aria-label="Next species at this hotspot"
                        >
                            <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                        </button>
                    </div>
                : null}
            </div>
            {showHotspotControls ?
                <div className="infobox-single-observation__measurement" aria-hidden="true">
                    {hotspotMarkers.map((marker, index) => {
                        const markerCardData = getMarkerCardData(marker, index);
                        if (!markerCardData) return null;

                        return (
                            <div
                                className="infobox-single-observation__measurement-card"
                                key={`${marker.properties.locationId}-${marker.properties.taxonomicOrder}-${marker.properties.date}-${marker.properties.time}-${index}`}
                                ref={element => {
                                    measureCardRefs.current[index] = element;
                                }}
                            >
                                <ObservationCard
                                    image={markerCardData.image}
                                    kicker={markerCardData.kicker}
                                    title={markerCardData.title}
                                    copy={markerCardData.copy}
                                    badges={markerCardData.badges}
                                    familyLink={markerCardData.familyLink}
                                    sourceUrl={markerCardData.sourceUrl}
                                    sourceLabel="Species Details"
                                    macaulayCatalogNumbers={markerCardData.macaulayCatalogNumbers}
                                />
                            </div>
                        );
                    })}
                </div>
            : null}
        </>
    )
}
